import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Task } from '../api';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Program } from '../../Reports/NewReportDialog';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { getAllocationStates, InvestmentSplitWithAnswers } from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import InvestmentSplit from './InvestmentSplit';
import AllocationQuestions from './AllocationQuestions';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { NPOUser } from '../../ReportsBeta/NewProgram/api';
import DonorDetails from './DonorDetails';
import {
  amountsApproximatelyEqual,
  numberWithCommas,
} from 'shared/utils/numberUtils';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';
import {
  AllocationTask,
  AllocationReports,
  deleteAllocationTask,
  getAllocationByTask,
  getAllocationSignature,
  getTaskInvestmentId,
  setAllocationTask,
} from './allocationLocalStorage';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  allocationRequest,
  AllocationRequestResponse,
  deleteAllocationWizardProgress,
  getAllocationWizardProgress,
  setAllocationWizardProgress,
} from './api';
import { removeConnections, useClient } from 'shared/apollo';
import {
  getReportAllocations,
  ReportAllocations,
  ReportAPIResponse,
} from '../../ReportsBeta/api';
import { ApolloQueryResult } from 'apollo-boost';
import AllocationConfirmationDialog from './AllocationConfirmationDialog';
import AllocationReportCreation, {
  AllocationReportCreationProps,
} from './AllocationReportCreation';

const removeReportConnections = (
  r: ApolloQueryResult<{
    reportsTodo: ReportAPIResponse[];
  }>,
) => {
  const reports = removeConnections(r.data.reportsTodo) as ReportAPIResponse[];
  return reports.filter(x => x.totalDonations > 0);
};

const getReportAllocationsFilters = (
  investmentSplitFirstStep: InvestmentSplitWithAnswers[],
): ReportAllocations[] => {
  const result: ReportAllocations[] = [];
  for (const i of investmentSplitFirstStep) {
    if (i.year && i.program?.name) {
      result.push({
        programId: i.program.Id,
        year: i.year,
      });
    }
  }
  return result;
};

interface AllocationProps {
  task: Task;
  open: boolean;
  programNameByDonor?: string;
  yearByDonor?: string;
  token?: string;
  allPrograms: RequestState<{
    allPrograms: Program[];
  }>;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  allReports: AllocationReportCreationProps['allReports'];
  reloadTasks: () => unknown;
  reloadPrograms: () =>
    | Promise<
        ApolloQueryResult<{
          allPrograms: Program[];
        }>
      >
    | undefined;
  reloadNPOUser: () => unknown;
  handleClose: () => unknown;
}

const isInvestmentSplitValid = (
  investments: InvestmentSplitWithAnswers[],
): {
  total: number;
  result: Required<InvestmentSplitWithAnswers>[];
} => {
  let total = 0;
  const result: Required<InvestmentSplitWithAnswers>[] = [];
  for (const i of investments) {
    const amount = Number(
      typeof i.amount === 'number' ? i.amount : i.amount.replace(/,/g, ''),
    );
    if (amount && (!i.program || !i.year)) {
      return { total: 0, result: [] };
    }
    if (i.program && i.year && i.percentage && amount) {
      total += amount;
      const program = i.program;
      result.push({
        amount,
        answers: i.answers,
        percentage: i.percentage,
        program,
        year: i.year,
      });
    }
  }
  return { total, result };
};

interface AllocationRenderProps {
  task: Task;
  open: boolean;
  totalAmount: number;
  yearByDonor: string | undefined;
  programNameByDonor: string | undefined;
  selectedProgram: Program | undefined;
  token?: string;
  allPrograms: RequestState<{
    allPrograms: Program[];
  }>;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  allReports: AllocationReportCreationProps['allReports'];
  reloadTasks: () => unknown;
  reloadPrograms: () =>
    | Promise<
        ApolloQueryResult<{
          allPrograms: Program[];
        }>
      >
    | undefined;
  reloadNPOUser: () => unknown;
  handleClose: () => unknown;
}

const StyledDiv = styled.div`
  display: flex;
  height: 100%;
  .investment-side-bar {
    overflow: auto;
    ${thinScrollbarStyles};
  }
  .allocation-wizard-wrapper {
    flex: 1;
    overflow: auto;
    ${thinScrollbarStyles};
    .investment-program-split-content {
      padding: ${SPACERS.large} ${SPACERS.medium} ${SPACERS.extraLarge}
        ${SPACERS.medium};
    }
  }
`;

const unableToAllocate = (
  <>Unable to allocate this investment. Please try again!</>
);
const styles = `.modal-header { border-bottom: 1px solid ${COLORS.ctaPrimaryBackground}; .close { padding-bottom: 0; } .modal-title { ` +
  `padding-bottom: ${SPACERS.small} } }`;
const AllocationRender: React.FC<AllocationRenderProps> = ({
  allPrograms,
  totalAmount,
  selectedProgram,
  task,
  allReports,
  yearByDonor,
  programNameByDonor,
  token,
  open,
  allNPOUsers,
  reloadNPOUser,
  reloadPrograms,
  reloadTasks,
  handleClose,
}) => {
  const client = useClient();
  const [showConfirmation, setShowConfirmation] = useState<
    | {
        reports: ReportAPIResponse[];
        result: Required<InvestmentSplitWithAnswers>[];
      }
    | undefined
  >(undefined);
  const [requestReportState, makeReportRequest] = useGraphQLRequest<unknown>();
  const [requestState, makeRequest] = useGraphQLRequest<
    AllocationRequestResponse,
    { task: Task; investment: Required<InvestmentSplitWithAnswers>[] }
  >();
  const allocation = useMemo(() => getAllocationByTask(task), [task]);
  const investmentId = useMemo(() => getTaskInvestmentId(task), [task]);
  const [investmentFinalSplitIndex, setInvestmentFinalSplitIndex] = useState<
    number
  >(0);
  const [investmentFinalSplit, setInvestmentFinalSplit] = useState<
    Required<InvestmentSplitWithAnswers>[] | undefined
  >(allocation?.allocation);
  const [errorText, setErrorText] = useState<JSX.Element | undefined>(
    undefined,
  );
  const [reports, setReports] = useState<AllocationReports>(
    allocation?.reports || {},
  );
  const [finishedIndexes, setFinishedIndex] = useState<Set<number>>(
    allocation ? new Set(allocation.finishedIndexes) : new Set(),
  );
  const [isReportCreationDone, setIsReportCreationDone] = useState<{
    [investmentIndex: number]: boolean | undefined;
  }>(allocation?.isReportCreationDone || {});
  const [investmentSplitFirstStep, setInvestmentSplitFirstStep] = useState<
    InvestmentSplitWithAnswers[]
  >(
    selectedProgram
      ? [
          {
            year: yearByDonor,
            program: selectedProgram,
            amount: '',
            percentage: '',
            answers: [],
          },
        ]
      : [],
  );

  useEffect(() => {
    if (selectedProgram) {
      setInvestmentSplitFirstStep(s =>
        s.find(p => p.program?.Id === selectedProgram.Id)
          ? s
          : [
              ...s.filter(
                p => p.program || p.amount || p.year || p.answers.length,
              ),
              {
                year: yearByDonor,
                program: selectedProgram,
                amount: '',
                percentage: '',
                answers: [],
              },
            ],
      );
      return;
    }
    if (programNameByDonor) {
      setInvestmentSplitFirstStep(s =>
        s.length
          ? s
          : [{ program: undefined, amount: '', percentage: '', answers: [] }],
      );
    }
  }, [selectedProgram, programNameByDonor]);

  useEffect(() => {
    if (allocation || !client || !investmentId) return;
    let cancelled = false;
    getAllocationWizardProgress(client, investmentId)
      .then(r => {
        if (cancelled) return;
        const raw = r.data.allocationWizardProgress?.data;
        if (!raw) return;
        let parsed: AllocationTask | undefined;
        try {
          parsed = JSON.parse(raw) as AllocationTask;
        } catch {
          return;
        }
        if (
          !parsed ||
          !Array.isArray(parsed.finishedIndexes) ||
          !Array.isArray(parsed.allocation) ||
          !parsed.reports ||
          !parsed.isReportCreationDone
        ) {
          return;
        }
        if (parsed.signature !== getAllocationSignature(task)) {
          deleteAllocationWizardProgress(client, investmentId).catch(() => {});
          return;
        }
        setAllocationTask(
          task,
          new Set(parsed.finishedIndexes),
          parsed.reports,
          parsed.allocation,
          parsed.isReportCreationDone,
        );
        setInvestmentFinalSplit(parsed.allocation);
        setReports(parsed.reports);
        setFinishedIndex(new Set(parsed.finishedIndexes));
        setIsReportCreationDone(parsed.isReportCreationDone);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [allocation, client, investmentId, task]);

  useEffect(() => {
    if (!investmentFinalSplit) return;
    setAllocationTask(
      task,
      finishedIndexes,
      reports,
      investmentFinalSplit,
      isReportCreationDone,
    );
    if (!client || !investmentId) return;
    setAllocationWizardProgress(client, investmentId, {
      reports,
      allocation: investmentFinalSplit,
      finishedIndexes: [...finishedIndexes],
      isReportCreationDone,
      signature: getAllocationSignature(task),
    }).catch(() => {});
  }, [
    task,
    client,
    investmentId,
    finishedIndexes,
    reports,
    investmentFinalSplit,
    isReportCreationDone,
  ]);

  useEffect(() => {
    if (requestState.type === 'REQUEST_START') {
      setErrorText(undefined);
      return;
    }
    if (requestState.type === 'REQUEST_ERROR') {
      setErrorText(<>Unable to allocate this investment. Please try again!</>);
      return;
    }
    if (
      requestState.type !== 'REQUEST_SUCCESS' ||
      !requestState.data.allocationRequest.success
    ) {
      return;
    }
    const extra: {
      task: Task;
      investment: Required<InvestmentSplitWithAnswers>[];
    } = requestState.extra;
    if (!extra) return;
    setReports(s => {
      const newState = { ...s };
      requestState.data.allocationRequest?.reportStatusList.forEach((a, i) => {
        const investment = extra.investment[i];
        const investmentYear = Number(investment.year);
        const reportId = reports[investment.program.name]?.[investmentYear];
        if (!investment) return;
        if (!newState[investment.program.name]) {
          newState[investment.program.name] = {};
        }
        // Backend is creating new report when allocating.
        // Hence we had to do reportId || a.reportId
        // Once that is fixed, it will be a.reportId
        newState[investment.program.name]![investmentYear] =
          reportId || a.reportId;
      });
      return newState;
    });
  }, [requestState, reloadTasks]);

  const handleInvestmentIndexChange = useCallback(
    (index: number) => {
      setInvestmentFinalSplitIndex(index);
    },
    [setInvestmentFinalSplitIndex],
  );

  const handleBack = useCallback(() => {
    investmentFinalSplit && setInvestmentSplitFirstStep(investmentFinalSplit);
    setInvestmentFinalSplit(undefined);
    setIsReportCreationDone(s => ({
      ...s,
      [investmentFinalSplitIndex]: false,
    }));
  }, [
    investmentFinalSplit,
    investmentFinalSplitIndex,
    setIsReportCreationDone,
    setInvestmentFinalSplit,
    setInvestmentSplitFirstStep,
  ]);

  const handleSaveSplit = useCallback(() => {
    setErrorText(undefined);
    const result = isInvestmentSplitValid(investmentSplitFirstStep);
    if (
      result.total === 0 ||
      !amountsApproximatelyEqual(result.total, totalAmount)
    ) {
      setErrorText(
        <>
          The total of all allocations should be{' '}
          <strong>${numberWithCommas(totalAmount)}</strong>, and every{' '}
          <strong>program</strong> is selected based on its respective{' '}
          <strong>year</strong>.
          <br />
          The selected total amount is{' '}
          <strong>${numberWithCommas(result.total)}</strong>
        </>,
      );
      return;
    }
    const reportAllocations = getReportAllocationsFilters(
      investmentSplitFirstStep,
    );
    makeReportRequest(
      getReportAllocations(client!, reportAllocations)
        .then(r => {
          const reports = removeReportConnections(r);
          if (reports.length === 0) {
            setInvestmentFinalSplit(result.result);
            return;
          }
          setShowConfirmation({
            reports,
            result: result.result,
          });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return reports as any;
        })
        .catch(() => {
          setErrorText(unableToAllocate);
        }),
    );
  }, [investmentSplitFirstStep, totalAmount, setErrorText]);

  const handleAllocationSave = useCallback(
    (investment: Required<InvestmentSplitWithAnswers>[] | undefined) => {
      if (!client || !investment) return;
      const isImpactSnapshot = !!(
        task.invitation?.investment?.portfolio?.isImpactSnapshot ||
        task.allocation?.investment?.portfolio?.isImpactSnapshot
      );
      const request = allocationRequest(client, task, investment, reports, token, isImpactSnapshot);
      makeRequest(request, { task, investment });
      return request;
    },
    [task, client, reports, token],
  );

  const handleFinalSave = useCallback(() => {
    deleteAllocationTask(task);
    if (client && investmentId) {
      deleteAllocationWizardProgress(client, investmentId).catch(() => {});
    }
    handleClose();
    reloadTasks();
  }, [task, client, investmentId, handleClose, reloadTasks]);

  // The remainder is what this pass must allocate, and it is already resolved
  // from either task shape by the caller.
  const totalAmountString = `$${numberWithCommas(totalAmount.toFixed(2))}`;

  const {
    isAllocationRecordsValid,
    allocationAmountState,
    allocationRecords,
  } = useMemo(() => {
    return getAllocationStates(investmentSplitFirstStep, totalAmount);
  }, [investmentSplitFirstStep, totalAmount]);

  const isNextButtonDisabled =
    (investmentFinalSplit &&
      investmentFinalSplit.length !== finishedIndexes.size) ||
    allocationAmountState === 'error' ||
    !isAllocationRecordsValid;

  const handleReportCreationNext = useCallback(
    (report?: { year: number; reportId: number; programName: string }) => {
      setIsReportCreationDone(s => ({
        ...s,
        [investmentFinalSplitIndex]: true,
      }));
      if (report) {
        setReports(s => ({
          ...s,
          [report.programName]: {
            ...s[report.programName],
            [report.year]: report.reportId,
          },
        }));
      }
    },
    [investmentFinalSplitIndex, setIsReportCreationDone, setReports],
  );

  const allProgramsArray = allPrograms.data?.allPrograms || [];

  const isImpactSnapshot = !!(
    task.invitation?.investment?.portfolio?.isImpactSnapshot ||
    task.allocation?.investment?.portfolio?.isImpactSnapshot
  );

  const isReportCreationStep = isImpactSnapshot || isReportCreationDone[investmentFinalSplitIndex];

  return (
    <TrueImpactModal
      showExpandButton={true}
      open={open}
      errorText={errorText}
      addStyles={styles}
      title="What does this contribution support?"
      next={
        isNextButtonDisabled
          ? undefined
          : {
              disabled: isNextButtonDisabled,
              text: investmentFinalSplit ? 'Done' : 'Next',
              isLoading:
                requestState.type === 'REQUEST_START' ||
                requestReportState.type === 'REQUEST_START',
              onClick: investmentFinalSplit ? handleFinalSave : handleSaveSplit,
            }
      }
      previous={
        investmentFinalSplit
          ? {
              onClick: handleBack,
              text: 'Back',
              disabled: !investmentFinalSplit,
            }
          : undefined
      }
      maxWidth="1300px"
      onClose={handleClose}
    >
      <StyledDiv
        className={investmentFinalSplit ? 'investment-answers-step' : undefined}
      >
        {showConfirmation && (
          <AllocationConfirmationDialog
            reports={showConfirmation.reports}
            handleNext={() => setInvestmentFinalSplit(showConfirmation.result)}
            handleClose={() => setShowConfirmation(undefined)}
          />
        )}
        <DonorDetails
          task={task}
          totalAmount={totalAmountString}
          finishedIndexes={finishedIndexes}
          programNameByDonor={programNameByDonor}
          investmentFinalSplit={investmentFinalSplit}
          investmentFinalSplitIndex={investmentFinalSplitIndex}
          handleInvestmentIndexChange={handleInvestmentIndexChange}
          className="investment-side-bar"
        />
        <div className="allocation-wizard-wrapper">
          {investmentFinalSplit && investmentFinalSplit.length > 0 ? (
            isReportCreationStep ? (
              <AllocationQuestions
                task={task}
                allocatingRequestState={requestState}
                investmentFinalSplitIndex={investmentFinalSplitIndex}
                totalAmount={totalAmount}
                selectedProgram={selectedProgram}
                allPrograms={allProgramsArray}
                finishedIndexes={finishedIndexes}
                reports={reports}
                totalFinishedInvestments={finishedIndexes.size}
                setFinishedIndex={setFinishedIndex}
                investmentSplit={investmentFinalSplit}
                handleInvestmentIndexChange={handleInvestmentIndexChange}
                setInvestmentFinalSplit={setInvestmentFinalSplit}
                handleBack={handleBack}
                handleAllocationSave={handleAllocationSave}
                isImpactSnapshot={isImpactSnapshot}
              />
            ) : (
              <AllocationReportCreation
                reports={reports}
                totalAmount={totalAmount}
                programsList={allPrograms}
                allReports={allReports}
                selectedProgram={selectedProgram}
                allPrograms={allProgramsArray}
                investment={investmentFinalSplit[investmentFinalSplitIndex]}
                investmentFinalSplitIndex={investmentFinalSplitIndex}
                handleNext={handleReportCreationNext}
                handleBack={handleBack}
                setReports={setReports}
              />
            )
          ) : (
            <InvestmentSplit
              task={task}
              totalAmount={totalAmount}
              selectedProgram={selectedProgram}
              programNameByDonor={programNameByDonor}
              allPrograms={allPrograms}
              investmentSplit={investmentSplitFirstStep}
              allNPOUsers={allNPOUsers}
              isAllocationRecordsValid={isAllocationRecordsValid}
              allocationAmountState={allocationAmountState}
              allocationRecords={allocationRecords}
              finishedIndexes={finishedIndexes}
              reloadNPOUser={reloadNPOUser}
              reloadPrograms={reloadPrograms}
              reloadTasks={reloadTasks}
              setInvestmentSplit={setInvestmentSplitFirstStep}
              className="investment-program-split-content"
            />
          )}
        </div>
      </StyledDiv>
    </TrueImpactModal>
  );
};

const Allocation: React.FC<AllocationProps> = ({
  task,
  open,
  allReports,
  allPrograms,
  allNPOUsers,
  yearByDonor,
  programNameByDonor,
  token,
  reloadTasks,
  reloadNPOUser,
  reloadPrograms,
  handleClose,
}) => {
  const isLoading =
    allPrograms.type === 'REQUEST_INIT' ||
    allPrograms.type === 'REQUEST_START' ||
    allNPOUsers.type === 'REQUEST_INIT' ||
    allNPOUsers.type === 'REQUEST_START';
  if (isLoading) {
    if (allPrograms.type === 'REQUEST_INIT') {
      reloadPrograms();
      return <TrueImpactLoading />;
    }
    if (allNPOUsers.type === 'REQUEST_INIT') {
      reloadNPOUser();
      return <TrueImpactLoading />;
    }
  }

  if (allPrograms.type === 'REQUEST_ERROR') {
    return (
      <TrueImpactText color="critical">
        {allPrograms.error.message}
      </TrueImpactText>
    );
  }

  // The remainder is owned by the server. Fall back to the local difference
  // only for payloads that predate unallocatedAmount.
  const investmentDetails =
    task.invitation?.investment || task.allocation?.investment;
  const totalAmount =
    investmentDetails?.unallocatedAmount !== undefined
      ? investmentDetails.unallocatedAmount
      : Math.max(
          (investmentDetails?.donation || 0) -
            (investmentDetails?.totalAllocated || 0),
          0,
        );
  const p = programNameByDonor || investmentDetails?.programNameByDonor;
  const selectedProgram = allPrograms.data?.allPrograms.find(x => x.name === p);

  return (
    <AllocationRender
      allPrograms={allPrograms}
      allNPOUsers={allNPOUsers}
      reloadNPOUser={reloadNPOUser}
      reloadPrograms={reloadPrograms}
      reloadTasks={reloadTasks}
      handleClose={handleClose}
      open={open}
      yearByDonor={yearByDonor}
      allReports={allReports}
      totalAmount={totalAmount}
      programNameByDonor={p}
      selectedProgram={selectedProgram}
      task={task}
      token={token}
    />
  );
};

export default Allocation;
